import { Injectable } from '@angular/core';
import {
  HttpBackend,
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams,
  HttpResponse,
} from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, tap } from 'rxjs/operators';
import { AuthService } from '../../../../core/services/auth.service';
import { ConfigService } from '../../../../../@vex/services/config.service';
import { UserProfileModel } from '../../../../core/models/user-profile.model';
import { buildSupplierApiPath, SupplierApiRoute } from './supplier-api-tester.catalog';

export interface SupplierApiTesterRequest {
  route: SupplierApiRoute;
  pathParams?: Record<string, string>;
  queryParams?: Record<string, string | number>;
  body?: unknown;
}

/** Un appel envoyé à l'API, tel qu'affiché dans le journal. */
export interface SupplierApiTesterResult {
  id: number;
  sentAt: Date;
  method: string;
  url: string;
  requestBody: unknown;
  /** 0 quand la requête n'a pas abouti (réseau, serveur arrêté). */
  status: number;
  statusText: string;
  durationMs: number;
  headers: { name: string; value: string }[];
  /** JSON parsé, ou texte brut si la réponse n'est pas du JSON. */
  body: unknown;
}

/** En-têtes utiles au testeur : traçabilité et limite de débit. */
const DISPLAYED_HEADERS = [
  'x-request-id',
  'ratelimit-policy',
  'ratelimit-limit',
  'ratelimit-remaining',
  'ratelimit-reset',
];

const KEY_STORAGE_PREFIX = 'supplier-api-tester:key:';

const JOURNAL_MAX_ENTRIES = 50;

/**
 * Appels de l'API fournisseur depuis l'onglet de test, réservé aux comptes Predik hors production.
 * Porte la clé du fournisseur affiché et le journal des appels, partagés par tous les écrans de l'onglet.
 */
@Injectable({
  providedIn: 'root',
})
export class SupplierApiTesterService {
  /**
   * Client sans intercepteurs : l'API fournisseur s'authentifie par clé (`Authorization: Bearer`).
   * Les intercepteurs de l'application y ajouteraient le jeton Azure AD et déconnecteraient
   * l'utilisateur sur le moindre 401 (clé révoquée, clé invalide).
   */
  private readonly http: HttpClient;
  private readonly apiKeySubject = new BehaviorSubject<string>('');
  private readonly journalSubject = new BehaviorSubject<SupplierApiTesterResult[]>([]);
  private supplierId: string;
  private nextJournalId = 1;

  /** Temporisé : une saisie au clavier ne relance pas un appel à chaque caractère. */
  readonly apiKey$ = this.apiKeySubject.pipe(debounceTime(400), distinctUntilChanged());
  readonly journal$ = this.journalSubject.asObservable();

  constructor(
    httpBackend: HttpBackend,
    private readonly authService: AuthService,
    private readonly configService: ConfigService,
  ) {
    this.http = new HttpClient(httpBackend);
  }

  get apiKey(): string {
    return this.apiKeySubject.value;
  }

  isAvailable(): boolean {
    return this.isAvailableFor(this.authService.currentUserValue());
  }

  /** Variante pour les guards : charge l'utilisateur s'il ne l'est pas encore. */
  isAvailable$(): Observable<boolean> {
    return this.authService.currentUserValue$().pipe(map(user => this.isAvailableFor(user)));
  }

  /** Bascule sur un fournisseur : reprend sa clé si elle a été saisie pendant la session et vide le journal. */
  useSupplier(supplierId: string): void {
    if (supplierId === this.supplierId) return;
    this.supplierId = supplierId;
    this.journalSubject.next([]);
    this.apiKeySubject.next(this.loadKey(supplierId));
  }

  setApiKey(apiKey: string): void {
    const trimmed = apiKey?.trim() ?? '';
    if (trimmed === this.apiKey) return;
    this.saveKey(this.supplierId, trimmed);
    this.apiKeySubject.next(trimmed);
  }

  clearJournal(): void {
    this.journalSubject.next([]);
  }

  send(request: SupplierApiTesterRequest): Observable<SupplierApiTesterResult> {
    const { route } = request;
    const path = buildSupplierApiPath(route, request.pathParams);
    const params = new HttpParams({ fromObject: this.withoutEmptyValues(request.queryParams) });
    const url = params.keys().length ? `${path}?${params.toString()}` : path;
    const startedAt = performance.now();
    const toResult = (response: HttpResponse<string> | HttpErrorResponse) =>
      this.toResult(route.method, url, request.body, response, startedAt);

    return this.http
      .request(route.method, path, {
        body: request.body,
        params,
        headers: new HttpHeaders({ Authorization: `Bearer ${this.apiKey}` }),
        observe: 'response',
        responseType: 'text',
      })
      .pipe(
        map(toResult),
        catchError((error: HttpErrorResponse) => of(toResult(error))),
        tap(result => this.journalSubject.next([result, ...this.journalSubject.value].slice(0, JOURNAL_MAX_ENTRIES))),
      );
  }

  private isAvailableFor(user: UserProfileModel | null): boolean {
    return !!user?.isPredik && this.configService.env?.applicationEnvironment !== 'production';
  }

  /** La clé n'est jamais relisible côté serveur : on la garde pour la session du navigateur. */
  private loadKey(supplierId: string): string {
    try {
      return sessionStorage.getItem(KEY_STORAGE_PREFIX + supplierId) ?? '';
    } catch {
      return '';
    }
  }

  private saveKey(supplierId: string, apiKey: string): void {
    try {
      if (apiKey) {
        sessionStorage.setItem(KEY_STORAGE_PREFIX + supplierId, apiKey);
      } else {
        sessionStorage.removeItem(KEY_STORAGE_PREFIX + supplierId);
      }
    } catch {
      // Stockage indisponible (navigation privée) : la clé reste en mémoire le temps de l'onglet.
    }
  }

  private withoutEmptyValues(values: Record<string, string | number>): Record<string, string> {
    return Object.fromEntries(
      Object.entries(values ?? {})
        .map(([name, value]) => [name, `${value ?? ''}`.trim()])
        .filter(([, value]) => value !== ''),
    );
  }

  private toResult(
    method: string,
    url: string,
    requestBody: unknown,
    response: HttpResponse<string> | HttpErrorResponse,
    startedAt: number,
  ): SupplierApiTesterResult {
    const rawBody = response instanceof HttpErrorResponse ? response.error : response.body;
    return {
      id: this.nextJournalId++,
      sentAt: new Date(),
      method,
      url,
      requestBody: requestBody ?? null,
      status: response.status,
      statusText: response.statusText,
      durationMs: Math.round(performance.now() - startedAt),
      headers: DISPLAYED_HEADERS.filter(name => response.headers?.has(name)).map(name => ({
        name,
        value: response.headers.get(name),
      })),
      body: this.parseBody(rawBody),
    };
  }

  private parseBody(rawBody: unknown): unknown {
    if (typeof rawBody !== 'string') {
      // Échec réseau : Angular fournit un ProgressEvent, sans intérêt pour le testeur.
      return null;
    }
    if (rawBody === '') {
      return null;
    }
    try {
      return JSON.parse(rawBody);
    } catch {
      return rawBody;
    }
  }
}
